/**
 * ONE token refresh for the whole app.
 *
 * Refresh tokens are single-use: the server deletes the presented row and mints a new
 * pair. Three places used to call `POST /auth/refresh` on their own — AuthContext on
 * boot and on its expiry timer, the axios interceptor on any 401, and each could fire
 * in the same second. The loser presented a token the winner had just rotated and got
 * a 500 (DbUpdateConcurrencyException on the deleted row), and every attempt spent a
 * slot of nginx's auth throttle — which is how a page with two saved accounts stayed
 * stuck behind the entry ring after a sign-out: refresh, 429, wait, refresh, 429…
 *
 * Every caller awaits the same in-flight promise. The result says which of three
 * things happened, because only one of them ends the session:
 *   { accessToken }             — refreshed (or another tab already had; `rotated`)
 *   { rejected: true, status }  — the server REFUSED the token (400 / 401 / 403): over
 *   { retry: true, status, retryAfterMs?, error? } — could not go through (offline,
 *                                 a deploy's restart, a 5xx, the throttle's 429): keep
 *                                 the tokens and try again later.
 */
import { API_BASE } from '../utils/apiBase';
import { expiryFromAuth, syncActiveTokensFromCanonical } from './accountRegistry';

const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';

// A server that accepts a connection but never answers must also be retried.
export const REFRESH_TIMEOUT_MS = 15000;

let inFlight = null;

/** Store a fresh pair in the canonical keys and mirror it onto the active account. */
export function storeSessionTokens(data) {
  const accessToken = data?.accessToken;
  // Writing undefined here stores the string "undefined", which passes every
  // "is there a token?" check and then 401s forever.
  if (!accessToken) return null;
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (data.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  const expiry = expiryFromAuth(data);
  if (expiry) localStorage.setItem(TOKEN_EXPIRY_KEY, expiry);
  else localStorage.removeItem(TOKEN_EXPIRY_KEY);
  syncActiveTokensFromCanonical();
  return accessToken;
}

const retryAfterMsOf = (response) => {
  const raw = response?.headers?.get?.('Retry-After');
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
};

async function performRefresh() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return { rejected: true, status: 0 };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  let response;
  try {
    response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      signal: controller.signal,
    });
  } catch (error) {
    // fetch() only throws for a network-level failure — offline, DNS, connection
    // refused, our own timeout. The token is untouched by definition.
    return { retry: true, status: 0, error };
  } finally {
    clearTimeout(timer);
  }

  // Another tab may have rotated the token while this was pending, or the user
  // signed out / switched accounts. Never overwrite that newer session.
  const current = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (current !== refreshToken) {
    return current
      ? { accessToken: localStorage.getItem(TOKEN_KEY), rotated: true }
      : { rejected: true, status: 0, signedOut: true };
  }

  if (!response.ok) {
    // Only the server REJECTING the token means the session is over. A 502 because
    // the API is restarting mid-deploy, a 503, a gateway timeout, the auth
    // throttle's 429 — those say nothing about the token.
    if (response.status === 400 || response.status === 401 || response.status === 403) {
      return { rejected: true, status: response.status };
    }
    return { retry: true, status: response.status, retryAfterMs: retryAfterMsOf(response) };
  }

  let body;
  try {
    body = await response.json();
  } catch (error) {
    return { retry: true, status: response.status, error };
  }
  // The API answers { success, data: { accessToken, … } }.
  const data = body?.data || body;
  const accessToken = storeSessionTokens(data);
  if (!accessToken) return { retry: true, status: response.status };
  return { accessToken, data };
}

/** Refresh the active session once, however many callers ask at the same time. */
export function refreshSession() {
  if (!inFlight) {
    inFlight = performRefresh().finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Test seam: forget an in-flight refresh. */
export function _resetSessionRefresh() { inFlight = null; }
